/* Rank complete matching brand groups while keeping Shopify's native matching and filters. */
(() => {
  'use strict';
  if (window.FAFSearchRanking) return;

  const vendorParameter = 'filter.p.vendor';
  const cache = new Map();
  const spellingCache = new Map();
  const cacheLimit = 16;
  const cacheLifetime = 30000;
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
  const hasPhrase = (query, phrase) => Boolean(phrase) && (' ' + query + ' ').includes(' ' + phrase + ' ');
  const genericBrandWords = new Set(['furniture', 'mattress', 'mattresses', 'home', 'homes', 'collection', 'collections', 'living', 'design', 'designs', 'by', 'the', 'and', 'co', 'company', 'inc', 'llc', 'ltd', 'usa', 'manufacturing', 'international', 'industries', 'of', 'for']);
  const ambiguousBrandWords = new Set(['new', 'classic', 'american', 'global', 'standard', 'house', 'sleep', 'best', 'modern', 'national', 'united']);
  const parameters = value => new URLSearchParams(value instanceof URLSearchParams ? value.toString() : value || '');

  function abortIfNeeded(signal) {
    if (signal?.aborted) throw new DOMException('Search request cancelled', 'AbortError');
  }

  function brandSettings(config) {
    const vendors = [...new Set((config.vendors || []).filter(value => typeof value === 'string' && value.trim()))];
    const aliases = (config.priorityBrands || ['Ashley', 'Benchcraft', 'Signature Design']).map(normalize).filter(Boolean);
    return { vendors, aliases };
  }

  function explicitBrand(query, brands) {
    if (/\bvendor\s*:/i.test(query)) return true;
    const normalizedQuery = normalize(query);
    if (brands.aliases.some(alias => hasPhrase(normalizedQuery, alias))) return true;
    return brands.vendors.some(vendor => {
      const name = normalize(vendor);
      if (hasPhrase(normalizedQuery, name)) return true;
      const words = name.split(' ').filter(word => !genericBrandWords.has(word));
      if ((words.length > 1 || (words.length === 1 && !ambiguousBrandWords.has(words[0]))) && hasPhrase(normalizedQuery, words.join(' '))) return true;
      return words.some(word => word.length >= 4 && !ambiguousBrandWords.has(word) && hasPhrase(normalizedQuery, word));
    });
  }

  function sectionFromHTML(html, sectionId) {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const section = document.getElementById('shopify-section-' + sectionId) || document.querySelector('[data-faf-search-page][data-section-id]');
    const count = section?.querySelector('[data-faf-search-count]')?.dataset.fafSearchCount;
    if (!section || !/^\d+$/.test(count || '') || !Number.isSafeInteger(Number(count))) throw new Error('Invalid search response');
    return section;
  }

  function responseBrands(section, aliases) {
    let data;
    try { data = JSON.parse(section.querySelector('[data-faf-search-brands]')?.textContent || 'null'); } catch { /* Invalid metadata falls back to native ordering. */ }
    if (!data || data.parameter !== vendorParameter || !Array.isArray(data.values) || !data.values.length) throw new Error('Brand filters unavailable');
    const values = new Map();
    data.values.forEach(item => {
      if (typeof item.value !== 'string' || !item.value || typeof item.label !== 'string') throw new Error('Invalid brand filter');
      const preferred = aliases.some(alias => hasPhrase(normalize(item.label), alias) || hasPhrase(normalize(item.value), alias));
      if (values.has(item.value) && values.get(item.value) !== preferred) throw new Error('Ambiguous brand filter');
      values.set(item.value, preferred);
    });
    return {
      preferred: Array.from(values).filter(([, preferred]) => preferred).map(([value]) => value),
      other: Array.from(values).filter(([, preferred]) => !preferred).map(([value]) => value),
    };
  }

  async function requestSection(params, sectionId, signal, searchUrl) {
    abortIfNeeded(signal);
    const url = new URL(searchUrl, window.location.origin);
    url.search = parameters(params).toString();
    url.searchParams.set('section_id', sectionId);
    const scope = [window.Shopify?.theme?.id || '', window.Shopify?.country || '', window.Shopify?.currency?.active || '', document.documentElement.lang].join('|');
    const key = scope + '|' + url.href;
    const saved = cache.get(key);
    if (saved && Date.now() - saved.time < cacheLifetime) {
      cache.delete(key);
      cache.set(key, saved);
      return sectionFromHTML(saved.html, sectionId);
    }
    if (saved) cache.delete(key);
    const response = await fetch(url.href, { signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Search request failed');
    const html = await response.text();
    abortIfNeeded(signal);
    const section = sectionFromHTML(html, sectionId);
    cache.set(key, { html, time: Date.now() });
    while (cache.size > cacheLimit) cache.delete(cache.keys().next().value);
    return section;
  }

  function readSection(section) {
    const count = Number(section.querySelector('[data-faf-search-count]')?.dataset.fafSearchCount);
    if (!Number.isSafeInteger(count) || count < 0) throw new Error('Invalid search count');
    const cards = Array.from(section.querySelectorAll('li[data-faf-search-product]'));
    const list = cards[0]?.parentElement || section.querySelector('ul.predictive-search__results-list, ul.collection-results');
    if (cards.some(card => card.parentElement !== list)) throw new Error('Invalid search card list');
    return { section, count, cards, list };
  }

  function oneSpellingChange(input, candidate) {
    if (input === candidate || input.slice(0, 4) !== candidate.slice(0, 4) || Math.abs(input.length - candidate.length) > 1) return false;
    if (input.length === candidate.length) {
      const different = Array.from(input).map((letter, index) => letter === candidate[index] ? -1 : index).filter(index => index >= 0);
      return different.length === 1 || (different.length === 2 && different[1] === different[0] + 1 &&
        input[different[0]] === candidate[different[1]] && input[different[1]] === candidate[different[0]]);
    }
    const shorter = input.length < candidate.length ? input : candidate;
    const longer = input.length < candidate.length ? candidate : input;
    let index = 0;
    while (index < shorter.length && shorter[index] === longer[index]) index += 1;
    return shorter.slice(index) === longer.slice(index + 1);
  }

  async function spellingCandidate(plan, signal, searchUrl) {
    const url = new URL(searchUrl, window.location.origin);
    url.pathname = url.pathname.replace(/\/$/, '') + '/suggest.json';
    url.search = new URLSearchParams({ q: plan.spellingTerm, 'resources[type]': 'query,product',
      'resources[limit]': '6', 'resources[limit_scope]': 'each', 'resources[options][fields]': 'title' }).toString();
    const scope = [window.Shopify?.theme?.id || '', window.Shopify?.country || '', window.Shopify?.currency?.active || '', document.documentElement.lang].join('|');
    const key = scope + '|' + url.href;
    const saved = spellingCache.get(key);
    if (saved && Date.now() - saved.time < cacheLifetime) return saved.candidate;
    abortIfNeeded(signal);
    const response = await fetch(url.href, { signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Spelling suggestions unavailable');
    const results = (await response.json())?.resources?.results;
    abortIfNeeded(signal);
    if (!results || !Array.isArray(results.products) || !Array.isArray(results.queries)) throw new Error('Invalid spelling suggestions');
    const input = normalize(plan.spellingTerm);
    const candidates = new Map();
    // Shopify proposes actual catalog names. Only an unambiguous one-letter
    // correction with the same first four letters can change this empty query.
    const terms = [...results.queries.slice(0, 6).map(item => item?.text),
      ...results.products.slice(0, 6).flatMap(item => typeof item?.title === 'string' ? item.title.match(/[\p{L}]+/gu) || [] : [])];
    terms.forEach(term => {
      if (typeof term !== 'string' || !/^[\p{L}]{5,30}$/u.test(term)) return;
      const word = normalize(term);
      if (oneSpellingChange(input, word)) candidates.set(word, term);
    });
    const candidate = candidates.size === 1 ? candidates.values().next().value : '';
    spellingCache.set(key, { candidate, time: Date.now() });
    while (spellingCache.size > cacheLimit) spellingCache.delete(spellingCache.keys().next().value);
    return candidate;
  }

  function markCorrection(section, correction) {
    [section, section.querySelector('[data-faf-search-page]'), section.querySelector('[data-faf-search-count]')].filter(Boolean).forEach(element => {
      element.dataset.fafSearchCorrection = correction;
      element.dataset.fafQueryMatch = 'spelling';
    });
  }

  async function matchingSection(base, plan, sectionId, signal, searchUrl) {
    if (plan.literalEngineQuery && plan.literalEngineQuery !== base.get('q')) {
      const literal = parameters(base);
      literal.set('q', plan.literalEngineQuery);
      try {
        const section = await requestSection(literal, sectionId, signal, searchUrl);
        // The total, rather than cards on this page, keeps a direct page 2+
        // request in the same result set even when that page is empty.
        if (readSection(section).count > 0) {
          section.dataset.fafQueryMatch = 'literal';
          return { params: literal, section };
        }
      } catch (error) {
        abortIfNeeded(signal);
        // An unavailable exact query must not disable native search recovery.
      }
    }
    const section = await requestSection(base, sectionId, signal, searchUrl);
    section.dataset.fafQueryMatch = plan.literalEngineQuery ? 'fuzzy' : 'native';
    if (plan.spellingTerm && plan.spellingScope && readSection(section).count === 0) {
      try {
        const candidate = await spellingCandidate(plan, signal, searchUrl);
        if (candidate) {
          const corrected = parameters(base);
          corrected.set('q', plan.spellingScope + ' AND (title:"' + candidate + '")');
          const verified = await requestSection(corrected, sectionId, signal, searchUrl);
          // Suggestions provide a spelling only. Existing Shopify search must
          // independently enforce outdoor/category/price/vendor constraints.
          const matching = readSection(verified);
          if (matching.count > 0 && matching.cards.every(card => {
            try { return hasPhrase(normalize(JSON.parse(card.querySelector('[data-faf-search-product-data]').textContent).title), normalize(candidate)); }
            catch { return false; }
          })) {
            markCorrection(verified, candidate);
            return { params: corrected, section: verified };
          }
        }
      } catch (error) {
        abortIfNeeded(signal);
        // An optional spelling lookup never replaces a valid empty response
        // with a network error, nor does it trigger another spelling attempt.
      }
    }
    return { params: base, section };
  }

  function cardId(card) {
    try {
      const value = JSON.parse(card.querySelector('[data-faf-search-product-data]')?.textContent || 'null');
      return value?.id ? String(value.id) : null;
    } catch { return null; }
  }

  function validCards(cards, expected) {
    const ids = cards.map(cardId);
    return cards.length === expected && ids.every(Boolean) && new Set(ids).size === ids.length;
  }

  function validPageCards(cards, expected, pageSize, cappedLastPage = false) {
    if (validCards(cards, expected)) return true;
    // The native capped response can return a full final page even though its
    // own pagination advertises only 1,000 items. Validate it, then use the
    // advertised window only; never expose a phantom page or extra positions.
    return cappedLastPage && expected > 0 && expected < pageSize && cards.length > expected &&
      cards.length <= pageSize && validCards(cards, cards.length);
  }

  function belongsToTier(card, preferred, aliases) {
    try {
      const data = JSON.parse(card.querySelector('[data-faf-search-product-data]')?.textContent || 'null');
      if (typeof data?.vendor !== 'string') return false;
      return aliases.some(alias => hasPhrase(normalize(data.vendor), alias)) === preferred;
    } catch { return false; }
  }

  function markPriority(section, status, preferred, other, countCapped = false) {
    const targets = [section, section.querySelector('[data-faf-search-page]'), section.querySelector('[data-faf-search-count]')].filter(Boolean);
    targets.forEach(target => {
      target.dataset.fafBrandPriority = status;
      if (Number.isSafeInteger(preferred)) target.dataset.fafPreferredCount = String(preferred);
      if (Number.isSafeInteger(other)) target.dataset.fafOtherCount = String(other);
      if (countCapped) target.dataset.fafBrandCountCapped = 'true';
    });
    return section;
  }

  function partitionParams(base, vendors) {
    const params = parameters(base);
    params.delete(vendorParameter);
    params.delete('page');
    vendors.forEach(vendor => params.append(vendorParameter, vendor));
    return params;
  }

  async function partitionSlice(first, params, start, length, pageSize, sectionId, signal, searchUrl, countCapped = false) {
    if (length <= 0) return [];
    const firstPage = Math.floor(start / pageSize) + 1;
    const lastPage = Math.floor((start + length - 1) / pageSize) + 1;
    const requests = [];
    for (let page = firstPage; page <= lastPage; page += 1) {
      if (page === 1) requests.push(Promise.resolve(first));
      else {
        const next = parameters(params);
        next.set('page', String(page));
        requests.push(requestSection(next, sectionId, signal, searchUrl).then(readSection));
      }
    }
    const parts = await Promise.all(requests);
    abortIfNeeded(signal);
    const pageCards = parts.map((part, index) => {
      const page = firstPage + index;
      const expected = Math.min(pageSize, Math.max(0, first.count - (page - 1) * pageSize));
      if (part.count !== first.count || !validPageCards(part.cards, expected, pageSize, countCapped && part.count === 1000)) throw new Error('Search partition changed');
      return part.cards.slice(0, expected);
    });
    return pageCards.flat().slice(start % pageSize, start % pageSize + length);
  }

  async function fetchSection(humanParams, sectionId, options = {}) {
    const api = window.FAFSearch;
    if (!api?.toEngineParams || !api?.plan) throw new Error('Search integration unavailable');
    const params = parameters(humanParams);
    const config = api.config || {};
    const searchUrl = config.searchUrl || window.location.pathname;
    const signal = options.signal;
    const pageSize = Number(options.pageSize) || (sectionId === 'faf-search-suggestions' ? 8 : 24);
    const pageValue = params.get('page') || '1';
    const page = /^\d+$/.test(pageValue) && Number(pageValue) > 0 ? Number(pageValue) : 1;
    const start = (page - 1) * pageSize;
    let base = api.toEngineParams(params);
    const plan = api.plan(params.get('q'));
    const brands = brandSettings(config);
    const sort = params.get('sort_by') || 'relevance';
    const bypass = !plan.productIntent || !plan.query || sort !== 'relevance' ||
      params.getAll(vendorParameter).some(value => value.trim()) || explicitBrand(plan.query, brands) ||
      !brands.aliases.length || !Number.isSafeInteger(start) || !Number.isSafeInteger(pageSize) || pageSize < 1;
    const matching = await matchingSection(base, plan, sectionId, signal, searchUrl);
    base = matching.params;
    const canonical = matching.section;
    if (bypass) return markPriority(canonical, 'native');
    abortIfNeeded(signal);
    try {
      const all = readSection(canonical);
      if (!all.count) return markPriority(canonical, 'native', 0, 0);
      // Shopify can group several raw vendors behind an opaque filter value.
      // Use the canonical response's actual Brand values, including group IDs.
      const groups = responseBrands(canonical, brands.aliases);
      const preferredParams = partitionParams(base, groups.preferred);
      const otherParams = partitionParams(base, groups.other);
      const partitions = await Promise.allSettled([
        groups.preferred.length ? requestSection(preferredParams, sectionId, signal, searchUrl).then(readSection) : Promise.resolve({ count: 0, cards: [] }),
        groups.other.length ? requestSection(otherParams, sectionId, signal, searchUrl).then(readSection) : Promise.resolve({ count: 0, cards: [] }),
      ]);
      abortIfNeeded(signal);
      if (partitions.some(result => result.status !== 'fulfilled')) throw new Error('Brand ordering unavailable');
      const preferred = partitions[0].value;
      const other = partitions[1].value;
      const expected = Math.min(pageSize, Math.max(0, all.count - start));
      // This storefront reports 1,000 for a saturated result count. A separately
      // filtered group can therefore also report 1,000 even when another group
      // has matches. Keep the canonical 1,000-item pagination window and rank
      // only that window; ordinary count disagreements still fail closed.
      const countCapped = all.count === 1000 && preferred.count <= 1000 && other.count <= 1000 &&
        (preferred.count === 1000 || other.count === 1000) && preferred.count + other.count > all.count;
      if ((!countCapped && preferred.count + other.count !== all.count) || !validPageCards(all.cards, expected, pageSize, countCapped)) throw new Error('Search partitions do not match');
      if (!expected || !preferred.count || !other.count) return markPriority(canonical, 'native', preferred.count, other.count, countCapped);
      if (!all.list) throw new Error('Missing search card list');
      const preferredLength = Math.min(expected, Math.max(0, preferred.count - start));
      const otherLength = expected - preferredLength;
      const slices = await Promise.all([
        partitionSlice(preferred, preferredParams, start, preferredLength, pageSize, sectionId, signal, searchUrl, countCapped),
        partitionSlice(other, otherParams, Math.max(0, start - preferred.count), otherLength, pageSize, sectionId, signal, searchUrl, countCapped),
      ]);
      abortIfNeeded(signal);
      const cards = slices.flat();
      if (!validCards(cards, expected) || slices[0].some(card => !belongsToTier(card, true, brands.aliases)) || slices[1].some(card => !belongsToTier(card, false, brands.aliases))) throw new Error('Incomplete search ordering');
      all.list.replaceChildren(...cards.map((card, index) => {
        const copy = card.cloneNode(true);
        if (copy.id) copy.id += '-ranked-' + index;
        return copy;
      }));
      return markPriority(canonical, 'applied', preferred.count, other.count, countCapped);
    } catch (error) {
      abortIfNeeded(signal);
      // A brand-ordering issue must never hide or broaden otherwise valid matches.
      // Discard stale or inconsistent partitions so the next attempt can recover.
      cache.clear();
      return markPriority(canonical, 'fallback');
    }
  }

  // Search totals are estimates and can change between otherwise identical
  // requests. Progressive search follows each native next-page link instead
  // of using those totals as offsets across the two brand groups.
  async function createStream(humanParams, sectionId, options = {}) {
    const api = window.FAFSearch;
    if (!api?.toEngineParams || !api?.plan || sectionId === 'faf-search-suggestions') return null;
    const params = parameters(humanParams);
    params.delete('page');
    const plan = api.plan(params.get('q'));
    const size = Number(options.pageSize) || 24;
    if (!plan.productIntent || !plan.query || !Number.isSafeInteger(size) || size < 1 || size > 50) return null;
    const config = api.config || {};
    const searchUrl = config.searchUrl || window.location.pathname;
    const searchPath = new URL(searchUrl, window.location.origin).pathname;
    let base = parameters(api.toEngineParams(params));
    base.delete('page');
    const brands = brandSettings(config);
    const matching = await matchingSection(base, plan, sectionId, options.signal, searchUrl);
    base = matching.params;
    const canonical = matching.section;
    const all = readSection(canonical);
    if (!all.list || !all.cards.length || canonical.querySelector('.template-search__item:not([data-faf-search-product])')) return null;
    const useGroups = (params.get('sort_by') || 'relevance') === 'relevance' &&
      !params.getAll(vendorParameter).some(value => value.trim()) &&
      !explicitBrand(plan.query, brands) && brands.aliases.length > 0;
    let groups;
    if (useGroups) {
      try { groups = responseBrands(canonical, brands.aliases); } catch { return null; }
    }
    const grouped = Boolean(groups?.preferred.length && groups?.other.length);
    const cursor = (values, preferred, seed = null) => ({
      params: values, preferred, page: 0, nextPage: 1,
      done: false, probe: false, buffer: [], seen: new Set(), seed,
    });
    let state = {
      cursors: grouped ? [
        cursor(partitionParams(base, groups.preferred), true),
        cursor(partitionParams(base, groups.other), false),
      ] : [cursor(parameters(base), null, canonical)],
      tier: 0, loaded: 0, seen: new Set(), done: false,
    };
    let busy = false;
    const stream = {};
    const copyState = source => ({
      ...source,
      seen: new Set(source.seen),
      cursors: source.cursors.map(item => ({ ...item, params: parameters(item.params), buffer: item.buffer.slice(), seen: new Set(item.seen) })),
    });

    function readNativePage(section, item) {
      const part = readSection(section);
      if (section.querySelector('.template-search__item:not([data-faf-search-product])') ||
        part.cards.length > size || !validCards(part.cards, part.cards.length) ||
        (item.preferred !== null && part.cards.some(card => !belongsToTier(card, item.preferred, brands.aliases)))) {
        throw new Error('Invalid progressive search page');
      }
      const pageElement = section.querySelector('.pagination-wrapper[data-page]');
      const nextLink = section.querySelector('a.pagination__item--next[href]');
      if (pageElement && Number(pageElement.dataset.page) !== item.nextPage &&
        !(item.probe && !part.cards.length && !nextLink)) throw new Error('Unexpected native search page');
      let nextPage = null;
      if (nextLink) {
        const url = new URL(nextLink.getAttribute('href'), new URL(searchUrl, window.location.origin));
        const value = url.searchParams.get('page') || '';
        const sameValues = key => JSON.stringify(url.searchParams.getAll(key).sort()) === JSON.stringify(item.params.getAll(key).sort());
        const scopedKeys = Array.from(url.searchParams.keys()).filter(key => key.startsWith('filter.') || key === 'sort_by');
        if (url.origin !== window.location.origin || url.pathname !== searchPath || !/^\d+$/.test(value) ||
          !Number.isSafeInteger(Number(value)) || Number(value) !== item.nextPage + 1 || !part.cards.length ||
          !sameValues('q') || scopedKeys.some(key => !sameValues(key))) {
          throw new Error('Invalid native search continuation');
        }
        nextPage = Number(value);
      }
      // A fluctuating estimate can suppress Next exactly at a full-page
      // boundary. Verify that endpoint with one bounded native lookahead;
      // partial pages and the native 1,000-item window do not need a probe.
      const probe = !nextLink && part.cards.length === size && item.nextPage * size < 1000;
      if (probe) nextPage = item.nextPage + 1;
      return { cards: part.cards, nextPage, probe };
    }

    function responseSection(cards, draft) {
      const section = canonical.cloneNode(true);
      const list = readSection(section).list;
      list.replaceChildren(...cards.map(card => card.cloneNode(true)));
      const totalEstimate = draft.done ? draft.loaded : Math.min(1000, Math.max(all.count, draft.loaded + 1));
      section.querySelectorAll('[data-faf-search-count]').forEach(element => {
        element.dataset.fafSearchCount = String(totalEstimate);
      });
      const wrapper = section.matches('[data-faf-search-page]') ? section : section.querySelector('[data-faf-search-page]');
      if (wrapper) {
        wrapper.dataset.fafStreamLoaded = String(draft.loaded);
        wrapper.dataset.fafStreamDone = String(draft.done);
      }
      // The facet HTML always comes from the unpartitioned query. Pagination
      // remains a native fallback, with a current logical-page next link.
      const pagination = section.querySelector('.pagination-wrapper');
      if (pagination) {
        const logicalPage = Math.max(1, Math.ceil(draft.loaded / size));
        pagination.dataset.page = String(logicalPage);
        const current = pagination.querySelector('[aria-current="page"]');
        if (current) current.textContent = String(logicalPage);
        const next = pagination.querySelector('a.pagination__item--next[href]');
        if (draft.done) next?.closest('li')?.remove();
        else if (next) {
          const url = new URL(next.getAttribute('href'), new URL(searchUrl, window.location.origin));
          url.searchParams.set('page', String(logicalPage + 1));
          next.setAttribute('href', url.pathname + url.search);
        }
      }
      markPriority(section, grouped ? 'applied' : 'native');
      return { section, done: draft.done, loaded: draft.loaded, totalEstimate };
    }

    async function next(nextOptions = {}) {
      if (busy) throw new Error('Progressive search is already loading');
      const signal = nextOptions.signal || options.signal;
      abortIfNeeded(signal);
      if (state.done) return responseSection([], state);
      busy = true;
      const draft = copyState(state);
      const cards = [];
      const target = Math.min(size, 1000 - draft.loaded);
      let requests = 0;
      const fillCursor = async item => {
        if (++requests > 8) throw new Error('Too many sparse search pages');
        const query = parameters(item.params);
        if (item.nextPage > 1) query.set('page', String(item.nextPage));
        const native = item.seed || await requestSection(query, sectionId, signal, searchUrl);
        abortIfNeeded(signal);
        item.seed = null;
        const page = readNativePage(native, item);
        const ids = page.cards.map(cardId);
        if (ids.some(id => draft.seen.has(id) && !item.seen.has(id))) throw new Error('Product crossed progressive search tiers');
        // Native page boundaries can overlap while the matching catalog changes.
        // Keep the first occurrence within this tier and fill the batch from its
        // real continuation; a wholly repeated page cannot make safe progress.
        const freshCards = page.cards.filter(card => !item.seen.has(cardId(card)));
        if (page.cards.length && !freshCards.length) throw new Error('Repeated page in progressive search');
        freshCards.forEach(card => {
          const id = cardId(card);
          draft.seen.add(id);
          item.seen.add(id);
        });
        item.buffer.push(...freshCards);
        item.page = item.nextPage;
        item.nextPage = page.nextPage;
        item.probe = page.probe;
        item.done = page.nextPage === null;
      };
      try {
        while (cards.length < target && draft.tier < draft.cursors.length) {
          const item = draft.cursors[draft.tier];
          if (item.buffer.length) {
            cards.push(...item.buffer.splice(0, target - cards.length));
          } else if (item.done) {
            draft.tier += 1;
          } else {
            // In the normal two-tier case a batch needs at most two native
            // pages. Bound recovery from unexpectedly sparse native pages.
            await fillCursor(item);
          }
        }
        // Empty buffers at a native endpoint can advance tiers without another
        // request, so a full final batch is correctly marked complete.
        while (draft.tier < draft.cursors.length) {
          const item = draft.cursors[draft.tier];
          if (item.buffer.length) break;
          if (item.done) draft.tier += 1;
          else if (item.probe && draft.loaded + cards.length < 1000) await fillCursor(item);
          else break;
        }
        draft.loaded += cards.length;
        draft.done = draft.loaded >= 1000 || draft.tier >= draft.cursors.length;
        if (!draft.done && cards.length !== target) throw new Error('Incomplete progressive search batch');
        const response = responseSection(cards, draft);
        abortIfNeeded(signal);
        // Commit only after every request and validation succeeds. A failure
        // retains the old native cursor and leftover cards for an exact retry.
        state = draft;
        Object.assign(stream, response);
        return response;
      } catch (error) {
        cache.clear();
        throw error;
      } finally { busy = false; }
    }
    stream.next = next;
    await next({ signal: options.signal });
    return stream;
  }

  window.FAFSearchRanking = { fetchSection, createStream };
})();
